import { ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  orderFollowedByRecency,
  type Exercise,
  type Program as ProgramT,
  type ProgramProgress as ProgramProgressT,
  type UnitsPreference,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { PagerNav } from "../primitives/PagerNav";
import { usePagerScroll } from "../primitives/usePagerScroll";
import { ProgramRunCard } from "./ProgramRunCard";

// The Plan highlight: a swipeable pager over the FOLLOWED programs, one
// ProgramRunCard per page (Antenore 2026-06-18). It replaces BOTH the single
// "today" hero AND the lower "In corso" list — those duplicated each other,
// and the hero had no day selection while the list card did. Reusing
// ProgramRunCard verbatim means every per-program control (next-up, Start,
// "Change week or session" day picker, View program, Edit, Unfollow) already
// works; the carousel adds the pager plus the shared PagerNav (arrows +
// dots + counter — essential on web, where a hidden-scrollbar pager can't
// be mouse-dragged; the controller logic lives in usePagerScroll, shared
// with the Home PR gallery since HR-9b).
//
// Order = orderFollowedByRecency (same sort pickTodaySession used), so the
// most-active cycle is card 0. One followed program → no nav, no paging
// (degrades to a single card). Zero → the component renders nothing and the
// host falls back to HomeHero's blank state.
export function ProgramHighlightCarousel({
  followedPrograms,
  progress,
  catalog,
  resolveName,
  unit,
  theme,
  dark,
  onStart,
  startingId,
  onUnfollow,
  onEdit,
  readOnlyProgramIds,
  onStopFollowing,
  currentProgramId,
  onSetCurrentBlock,
}: {
  followedPrograms: ProgramT[];
  progress: Record<string, ProgramProgressT | null> | null;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  unit: UnitsPreference;
  theme: UiTheme;
  dark: boolean;
  /** Mobile-only: starts (program, weekIdx, sessionIdx) on the logger. Omit
   *  on hosts with no logger (web) — ProgramRunCard hides the run affordances. */
  onStart?: (program: ProgramT, weekIdx: number, sessionIdx: number) => void;
  startingId?: string | null;
  onUnfollow: (program: ProgramT) => void;
  onEdit: (program: ProgramT) => void;
  /** Ids the caller FOLLOWS (useProgramFollowGate): those cards are
   *  read-only, so no pencil, and their ✕ calls `onStopFollowing` instead
   *  of `onUnfollow`. Absent ⇒ no card is read-only and every control
   *  behaves exactly as before. */
  readOnlyProgramIds?: ReadonlySet<string>;
  /** Drop the follow row. Only ever called for a read-only card; absent ⇒
   *  the ✕ falls back to `onUnfollow`, which is what a host that has not
   *  been taught about follows already does. */
  onStopFollowing?: (program: ProgramT) => void;
  /** AP-3: the athlete's pinned "active block" id (Profile.currentProgramId).
   *  The matching card shows its star filled. */
  currentProgramId?: string | null;
  /** Toggle a program as the active block. Omit to hide the star (web). */
  onSetCurrentBlock?: (program: ProgramT) => void;
}) {
  const { t } = useTranslation();
  const ordered = orderFollowedByRecency(followedPrograms, progress);
  const pager = usePagerScroll(ordered.length);
  const styles = makeStyles(theme);

  if (ordered.length === 0) return null;
  const multi = ordered.length > 1;

  return (
    <View style={styles.wrap} onLayout={pager.onLayout}>
      <ScrollView
        ref={pager.scrollRef}
        horizontal
        pagingEnabled={multi}
        scrollEnabled={multi}
        showsHorizontalScrollIndicator={false}
        onScroll={pager.onScroll}
        scrollEventThrottle={16}
        // Lock to the horizontal axis so a vertical drag on a card scrolls the
        // page, not the pager (the card's day/week chips wrap, they don't
        // scroll, so there's no inner horizontal scroller to fight).
        directionalLockEnabled
      >
        {pager.width > 0
          ? ordered.map((p, i) => {
              // ONE derivation per card, read by the pencil and by the ✕.
              const readOnly = readOnlyProgramIds?.has(p.id) ?? false;
              return (
              // Each page is the full viewport width (so paging snaps one card
              // at a time) but insets the card by s.s5 — the same horizontal
              // padding ProgramsPlanView's wrap gave these cards in the list,
              // so the carousel card lines up with the rest of the Plan content
              // instead of running edge-to-edge (Antenore 2026-06-18).
              // Off-screen pages leave the a11y tree — PagerNav is the screen
              // reader's navigation, not wandering onto invisible cards.
              <View
                key={p.id}
                style={[styles.page, { width: pager.width }]}
                importantForAccessibility={
                  i === pager.index ? "auto" : "no-hide-descendants"
                }
                accessibilityElementsHidden={i !== pager.index}
              >
                <ProgramRunCard
                  program={p}
                  progress={progress?.[p.id] ?? null}
                  catalog={catalog}
                  resolveName={resolveName}
                  unit={unit}
                  isStarting={startingId === p.id}
                  onStart={
                    onStart
                      ? (weekIdx, sessionIdx) => onStart(p, weekIdx, sessionIdx)
                      : undefined
                  }
                  onUnfollow={() =>
                    readOnly && onStopFollowing
                      ? onStopFollowing(p)
                      : onUnfollow(p)
                  }
                  onEdit={() => onEdit(p)}
                  readOnly={readOnly}
                  isCurrentBlock={currentProgramId === p.id}
                  onSetCurrentBlock={
                    onSetCurrentBlock ? () => onSetCurrentBlock(p) : undefined
                  }
                  theme={theme}
                  dark={dark}
                />
              </View>
              );
            })
          : null}
      </ScrollView>

      <View style={styles.navInset}>
        <PagerNav
          theme={theme}
          index={pager.index}
          count={ordered.length}
          onGoTo={pager.goTo}
          prevLabel={t("mobile.plan.carouselPrev", {
            defaultValue: "Previous program",
          })}
          nextLabel={t("mobile.plan.carouselNext", {
            defaultValue: "Next program",
          })}
          positionLabel={t("mobile.plan.carouselPosition", {
            n: pager.index + 1,
            total: ordered.length,
            defaultValue: "Program {{n}} of {{total}}",
          })}
          dotLabel={(i) =>
            t("mobile.plan.carouselPosition", {
              n: i + 1,
              total: ordered.length,
              defaultValue: "Program {{n}} of {{total}}",
            })
          }
        />
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    // The carousel renders chrome-less inside HomeView's hero box (HR-9), so
    // it carries its own vertical padding; the s5 page inset below doubles as
    // the box's horizontal padding.
    wrap: { width: "100%", paddingVertical: s.s4 },
    page: { paddingHorizontal: s.s5 },
    // PagerNav row inside the hero box — same s5 inset as the pages so a
    // long dots row can't run into the box border.
    navInset: { marginTop: s.s2, paddingHorizontal: s.s5 },
  });
}
